import * as React from "react";
import { styled } from "@mui/material/styles";
import ChevronRight from "apollo-react-icons/ChevronRight";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { QuestionnaireAccordionPropTypes } from "./types";
import { Box, Divider } from "@mui/material";
import { isEmpty } from "lodash";
import { copyToClipBoard, mergeStringWithNewLine, getFormattedTableData } from "../../utils/answerUtils";
import { addText, addNewLine, addHtml, addTable } from "../../actions";

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  () => ({
    border: `1px solid #d9d9d9 !important`,
    "&:before": {
      display: "none",
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ChevronRight sx={{ fontSize: "0.9rem" }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: "rgb(255, 255, 255)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "&.MuiAccordionSummary-root": {
    padding: theme.spacing(1),
  },
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

function getAnswer(answers: any, noAnswer: any) {
  if (noAnswer) return "No Answer";
  if (Array.isArray(answers[answers.length - 1].answer)) {
    return answers[answers.length - 1].answer.join(", ");
  }
  if (typeof answers[answers.length - 1].answer === "string") {
    return answers[answers.length - 1].answer;
  }
  return "";
}

function Question({ questionText, questionHTML, formattedText, answers, type, questionTableConfig }) {
  const noAnswer =
    isEmpty(answers) ||
    (Array.isArray(answers[answers.length - 1].answer) && answers[answers.length - 1].answer.length === 0) ||
    (typeof answers[answers.length - 1].answer === "string" && answers[answers.length - 1].answer.trim() === "") ||
    !answers[answers.length - 1].answer;

  const lastAnswer = answers[answers.length - 1];

  async function handleAddIconClick() {
      if (formattedText) {
        if (Office?.context?.host === Office.HostType.PowerPoint) {
          if (questionHTML || questionText) {
            copyToClipBoard(
              mergeStringWithNewLine(
                questionHTML || questionText,
                lastAnswer?.formattedAnswer?.htmlExport ||
                  lastAnswer?.formattedAnswer?.html ||
                  getAnswer(answers, noAnswer)
              )
            );
          }
        } else {
          let addQuestionResponse: any;
          if (questionHTML) {
            addQuestionResponse = await addHtml(questionHTML);
          } else {
            addQuestionResponse = await addText(questionText);
          }
          if (addQuestionResponse) {
            await addNewLine();
            if (type === 'table' && Office?.context?.host === Office.HostType.Word) {
              if (addQuestionResponse && answers.length > 0) {
                await addTable(getFormattedTableData(lastAnswer.answer));
              } else {
                await addTable(getFormattedTableData(questionTableConfig));
              }
            } else {
              if (!noAnswer) {
                let addAnswerResponse: any;
                if (
                  lastAnswer &&
                  lastAnswer.formattedAnswer &&
                  (lastAnswer.formattedAnswer.htmlExport || lastAnswer.formattedAnswer.html)
                ) {
                  addAnswerResponse = await addHtml(lastAnswer.formattedAnswer.htmlExport || lastAnswer.formattedAnswer.html);
                } else {
                  addAnswerResponse = await addText(
                    Array.isArray(answers[answers.length - 1].answer)
                      ? answers[answers.length - 1].answer.join(", ")
                      : answers[answers.length - 1].answer,
                    true
                  );
                }
                if (addAnswerResponse) {
                  await addNewLine();
                }
              }
            }
          }
        }
      } else {
        const addQuestionResponse = await addText(questionText, false);
        if (addQuestionResponse) { 
          if (type === 'table' && Office?.context?.host === Office.HostType.Word) {
            if (addQuestionResponse && answers.length > 0) {
              await addTable(getFormattedTableData(lastAnswer.answer));
            } else {
              await addTable(getFormattedTableData(questionTableConfig));
            }
          } else {
            if (!noAnswer) {
              await addText(
                Array.isArray(answers[answers.length - 1].answer)
                  ? answers[answers.length - 1].answer.join(", ")
                  : answers[answers.length - 1].answer,
                true
              );
            }
          }
        }
      }
  }

  return (
    <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Typography className="q-a-font">
        <Typography color="primary" sx={{ mr: "4px", display: "inline" }} component="span">
          Q:
        </Typography>
        {questionText}
      </Typography>
      <div>
        <IconButton size="small" onClick={handleAddIconClick}>
          <AddCircleIcon color="primary" />
        </IconButton>
      </div>
    </Box>
  );
}

function Answer({ answers, formattedText, type }) {
  const noAnswer =
    isEmpty(answers) ||
    (Array.isArray(answers[answers.length - 1].answer) && answers[answers.length - 1].answer.length === 0) ||
    (typeof answers[answers.length - 1].answer === "string" && answers[answers.length - 1].answer.trim() === "") ||
    !answers[answers.length - 1].answer;

  const lastAnswer = answers[answers.length - 1];
  async function handleAddIconClick() {
    if (type === 'table' && Office?.context?.host === Office.HostType.Word) {
      await addTable(getFormattedTableData(lastAnswer.answer));
    } else {
      if (formattedText) {
        if (!noAnswer) {
          if (
            lastAnswer &&
            lastAnswer.formattedAnswer &&
            (lastAnswer.formattedAnswer.htmlExport || lastAnswer.formattedAnswer.html)
          ) {
            Office?.context?.host === Office.HostType.PowerPoint
              ? copyToClipBoard(lastAnswer.formattedAnswer.htmlExport || lastAnswer.formattedAnswer.html)
              : await addHtml(lastAnswer.formattedAnswer.htmlExport || lastAnswer.formattedAnswer.html);
          } else {
            Office?.context?.host === Office.HostType.PowerPoint
              ? copyToClipBoard(getAnswer(answers, noAnswer))
              : await addText(getAnswer(answers, noAnswer), false);
          }
        }
      } else {
        await addText(getAnswer(answers, noAnswer), false);
      }
    }   
  }

  return (
    <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Typography fontStyle={noAnswer ? "italic" : "normal"} className="q-a-font">
        <Typography fontStyle="normal" color="primary" sx={{ mr: "4px", display: "inline" }} component="span">
          A:
        </Typography>
        {type === 'table' ? 'Table' : getAnswer(answers, noAnswer)}
      </Typography>
      <div>
        <IconButton size="small" disabled={noAnswer} onClick={handleAddIconClick}>
          <AddCircleIcon color={noAnswer ? "disabled" : "primary"} />
        </IconButton>
      </div>
    </Box>
  );
}

export default function QuestionnaireAccordion({
  info,
  onExpandToggle,
  formattedText,
}: QuestionnaireAccordionPropTypes) {
  return (
    <Accordion expanded={info.expanded} onChange={() => onExpandToggle(!info.expanded)} sx={{ mb: "8px" }}>
      <AccordionSummary>
        <Typography>
          {info.sectionName === "Questions_for_the_Customer_left_panel"
            ? "Questions for the Customer"
            : info.sectionName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {info.questions.map((question, qIndex) => (
          <>
            <Box sx={{ p: ".5rem" }} key={question.questionId}>
              <Question
                questionText={question.questionText}
                questionHTML={question.questionHTML}
                formattedText={formattedText}
                answers={question.answers}
                type={question.answerConfiguration.type}
                questionTableConfig={question.questionTableConfig}
              />
              <Answer answers={question.answers} formattedText={formattedText} type={question.answerConfiguration.type}/>
            </Box>
            {qIndex !== info.questions.length - 1 ? <Divider /> : null}
          </>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
